
import { useState } from "react";
import { useAppSelector } from "@/hooks/useAppSelector";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import FilterDropdown from "@/components/FilterDropdown";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface CreateJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (jobSpec: any) => void;
}

const CreateJobModal = ({ isOpen, onClose, onSubmit }: CreateJobModalProps) => {
  const [jobType, setJobType] = useState("EXTRACT_METRICS_BATCH");
  const [instruction, setInstruction] = useState("");
  const [attribute, setAttribute] = useState("");
  const [version, setVersion] = useState("");
  const [quarter, setQuarter] = useState("");
  const [force, setForce] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(["All"]);
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  const { companies } = useAppSelector((state) => state.supaCompanies);
  
  const uniqueCategories = ["All", ...Array.from(new Set(companies.map(company => company.category)))];

  const filteredCompanies = companies
    .filter(company => 
      (selectedCategories.includes("All") || selectedCategories.includes(company.category)) &&
      (searchQuery === "" || company.slug.toLowerCase().includes(searchQuery.toLowerCase()))
    );

  const getSlugsToProcess = () => {
    if (selectedSlugs.length > 0) {
      return selectedSlugs;
    } else {
      return filteredCompanies.map(company => company.slug);
    }
  };

  const handleSubmit = () => {
    const slugsToProcess = getSlugsToProcess();
    
    let jobSpec: any;
    
    if (jobType === "CLASSIFICATION") {
      jobSpec = {
        type: jobType,
        data: {
          prompt: instruction,
          slugs: slugsToProcess,
          attribute: attribute
        }
      };
    } else if (jobType === "EXTRACT_METRICS_BATCH") {
      jobSpec = {
        type: jobType,
        data: {
          version: version || null,
          quarter: quarter || null,
          slugs: slugsToProcess,
          force: force
        }
      };
      // Log the job spec to verify quarter is included
      console.log("Extract Metrics job spec:", JSON.stringify(jobSpec, null, 2));
    } else if (jobType === "PROCESS_METRIC_EVENTS") {
      jobSpec = {
        type: jobType,
        data: {
          slugs: slugsToProcess,
          quarter: quarter || null
        }
      };
      // Log the process metric events job spec
      console.log("Process Metric Events job spec:", JSON.stringify(jobSpec, null, 2));
    } else {
      jobSpec = {
        type: jobType,
        data: {
          prompt: instruction,
          slugs: slugsToProcess
        }
      };
    }
    
    onSubmit(jobSpec);
    onClose();
  };

  const handleCategoryChange = (value: string | string[]) => {
    setSelectedCategories(Array.isArray(value) ? value : [value]);
    setSelectedSlugs([]);
  };

  const toggleSlugSelection = (slug: string) => {
    if (selectedSlugs.includes(slug)) {
      setSelectedSlugs(selectedSlugs.filter(s => s !== slug));
    } else {
      setSelectedSlugs([...selectedSlugs, slug]);
    }
  };

  const clearSelectedSlugs = () => {
    setSelectedSlugs([]);
  };

  const isFormValid = () => {
    if (jobType === "CLASSIFICATION") {
      return instruction.trim() !== "" && attribute.trim() !== "";
    } else if (jobType === "EXTRACT_METRICS_BATCH") {
      return true; // (version.trim() !== "" || quarter.trim() !== "");
    } else if (jobType === "PROCESS_METRIC_EVENTS") {
      // For PROCESS_METRIC_EVENTS, we just need at least one company selected
      return getSlugsToProcess().length > 0;
    }
    return instruction.trim() !== "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Job</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="jobType" className="pl-2">Job Type</Label>
            <Select
              value={jobType}
              onValueChange={setJobType}
            >
              <SelectTrigger id="jobType">
                <SelectValue placeholder="Select job type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CLASSIFICATION">Classification</SelectItem>
                <SelectItem value="EXTRACT_METRICS_BATCH">Extract Metrics Batch</SelectItem>
                <SelectItem value="PROCESS_METRIC_EVENTS">Process Metric Events</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {jobType === "CLASSIFICATION" && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="instruction">Instruction</Label>
                <Textarea
                  id="instruction"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder="Enter the instruction for the job..."
                  className="resize-none"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="attribute">Attribute</Label>
                <Input
                  id="attribute"
                  value={attribute}
                  onChange={(e) => setAttribute(e.target.value)}
                  placeholder="Enter the attribute name..."
                />
              </div>
            </>
          )}

          {jobType === "EXTRACT_METRICS_BATCH" && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="e.g., 20241001"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="quarter">Quarter</Label>
                <Input
                  id="quarter"
                  value={quarter}
                  onChange={(e) => setQuarter(e.target.value)}
                  placeholder="e.g., 2024Q1"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <Label htmlFor="force" className="cursor-pointer">Force re-extraction</Label>
                  <span className="text-xs text-muted-foreground">
                    Override existing metrics data
                  </span>
                </div>
                <Switch
                  id="force"
                  checked={force}
                  onCheckedChange={setForce}
                />
              </div>
            </>
          )}

          {jobType === "PROCESS_METRIC_EVENTS" && (
            <>
              <div className="bg-blue-50 p-3 rounded-md">
                <div className="text-sm">
                  This job will process metric change events for the selected companies.
                  It analyzes the impact of metric changes on the pricing structure.
                </div>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="quarter">Quarter (Optional)</Label>
                <Input
                  id="quarter"
                  value={quarter}
                  onChange={(e) => setQuarter(e.target.value)}
                  placeholder="e.g., 2024Q1"
                />
                <div className="text-xs text-muted-foreground">
                  If specified, will only process events for this quarter
                </div>
              </div>
            </>
          )}

          <div className="grid gap-2">
            <Label className="pl-2">Categories</Label>
            <FilterDropdown
              label="Categories"
              options={uniqueCategories}
              value={selectedCategories}
              onChange={handleCategoryChange}
              isMulti={true}
              displayAllText="All Categories"
            />
          </div>

          <div className="grid gap-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="searchCompany">Filter Companies</Label>
              {selectedSlugs.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearSelectedSlugs}
                  className="flex items-center gap-1"
                >
                  <X className="h-3 w-3" /> Clear selection ({selectedSlugs.length})
                </Button>
              )}
            </div>
            <Input
              id="searchCompany"
              placeholder="Search by slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label>Select Specific Companies (Optional)</Label>
            <ScrollArea className="h-[200px] border rounded-md p-2">
              <div className="grid gap-1">
                {filteredCompanies.length === 0 ? (
                  <div className="text-sm text-muted-foreground p-2">No companies match your filters</div>
                ) : (
                  filteredCompanies.map(company => (
                    <div 
                      key={company.slug}
                      className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-gray-100 ${
                        selectedSlugs.includes(company.slug) ? 'bg-gray-100' : ''
                      }`}
                      onClick={() => toggleSlugSelection(company.slug)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{company.slug}</span>
                        <Badge variant="outline" className="text-xs">{company.category}</Badge>
                      </div>
                      {selectedSlugs.includes(company.slug) && (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          <div className="bg-gray-50 p-3 rounded-md">
            <div className="text-sm font-medium">Summary</div>
            <div className="text-sm text-muted-foreground mt-1">
              {selectedSlugs.length > 0 ? (
                <>
                  <span>{selectedSlugs.length} specifically selected companies will be processed</span>
                </>
              ) : (
                <>
                  <span>{getSlugsToProcess().length} companies will be processed based on category filters</span>
                </>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isFormValid()}>
            Create Job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateJobModal;
